import { useEffect, useState } from "react";
import { db } from "../../../firebase/firestore";
import {
  collection,
  onSnapshot,
  writeBatch,
  doc
} from "firebase/firestore";
import jsPDF from "jspdf";

function getItemWeightInGrams(item) {
  const sizeText = String(item?.sizeLabel || item?.selectedSize || item?.size || "");
  const fromLabel = Number((sizeText.match(/(\d+(?:\.\d+)?)/) || [])[1] || 0);
  const rawWeight = Number(item?.weight || item?.grams || item?.size || fromLabel || 0);
  if (!Number.isFinite(rawWeight) || rawWeight <= 0) return 0;
  return rawWeight;
}

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [production, setProduction] = useState({});

  useEffect(() => {
    const unsubItems = onSnapshot(collection(db, "items"), (snap) => {
      const itemsData = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        stockGrams: Number(d.data().stockGrams || 0),
      }));
      setItems(itemsData);
    });

    const unsubOrders = onSnapshot(collection(db, "orders"), (snap) => {
      const ordersData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOrders(ordersData);
      calculateProduction(ordersData);
    });

    return () => {
      unsubItems();
      unsubOrders();
    };
  }, []);

  // 🔥 PRODUCTION CALCULATION
  const calculateProduction = (ordersData) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const result = {};

    ordersData.forEach(order => {
      if (String(order.status || "").toLowerCase() === "cancelled") return;
      if (!order.deliveryDate) return;

      const d = new Date(order.deliveryDate);

      if (d.toDateString() === tomorrow.toDateString()) {
        order.items?.forEach(item => {
          const qty = Number(item?.qty || 0);
          const grams = qty * getItemWeightInGrams(item);
          if (!Number.isFinite(grams) || grams <= 0) return;

          if (!result[item.name]) result[item.name] = 0;
          result[item.name] += grams;
        });
      }
    });

    // 🔥 SORT (HIGH DEMAND FIRST)
    const sorted = Object.fromEntries(
      Object.entries(result).sort((a, b) => b[1] - a[1])
    );

    setProduction(sorted);
  };

  // 🔥 SAFE STOCK REDUCTION
  const reduceStockFromOrders = async () => {
    const deliveredPending = orders.filter(
      (order) =>
        String(order.status || "").toLowerCase() === "delivered" &&
        !order.inventoryAdjustedAt
    );

    if (!deliveredPending.length) {
      alert("No delivered orders pending stock update.");
      return;
    }

    const stockUsageByItemId = {};
    let skippedItems = 0;

    deliveredPending.forEach((order) => {
      (order.items || []).forEach((item) => {
        const key = item.id || item.name;
        if (!key) {
          skippedItems += 1;
          return;
        }

        const qty = Number(item.qty || 0);
        const grams = qty * getItemWeightInGrams(item);

        if (!grams) {
          skippedItems += 1;
          return;
        }
        stockUsageByItemId[key] = (stockUsageByItemId[key] || 0) + grams;
      });
    });

    const batch = writeBatch(db);

    let adjustedItems = 0;
    Object.entries(stockUsageByItemId).forEach(([itemKey, used]) => {
      const match = items.find((i) => i.id === itemKey || i.name === itemKey);
      if (!match) {
        skippedItems += 1;
        return;
      }

      const newStock = Math.max(0, Number(match.stockGrams || 0) - Number(used || 0));
      batch.update(doc(db, "items", match.id), { stockGrams: newStock });
      adjustedItems += 1;
    });

    deliveredPending.forEach((order) => {
      batch.update(doc(db, "orders", order.id), {
        inventoryAdjustedAt: new Date().toISOString(),
      });
    });

    await batch.commit();
    alert(`Stock updated. Adjusted items: ${adjustedItems}${skippedItems ? `, skipped: ${skippedItems}` : ""}.`);
  };

  // 🔥 STATUS
  const getStatus = (grams) => {
    if (grams <= 0) return "out";
    if (grams < 2000) return "low";
    return "ok";
  };

  // 🔥 PDF (UPGRADED TABLE STYLE)
  const exportProduction = () => {
    const pdf = new jsPDF();

    pdf.setFontSize(18);
    pdf.text("Shreeji Surti Swad - Production Sheet", 20, 20);

    let y = 40;

    pdf.setFontSize(12);
    pdf.text("Item", 20, y);
    pdf.text("Grams", 90, y);
    pdf.text("KG", 140, y);

    y += 10;

    let total = 0;

    Object.keys(production).forEach((name) => {
      const grams = production[name];
      const kg = (grams / 1000).toFixed(2);

      total += grams;

      pdf.text(name, 20, y);
      pdf.text(String(grams), 90, y);
      pdf.text(kg, 140, y);

      y += 10;
    });

    y += 10;
    pdf.setFontSize(14);
    pdf.text(`Total Production: ${(total / 1000).toFixed(2)} kg`, 20, y);

    pdf.save("production-sheet.pdf");
  };

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="inventory-page">

      <h1>📦 Smart Inventory & Production</h1>

      {/* 🔥 ACTION BUTTONS */}
      <div className="inventory-actions">
        <button onClick={reduceStockFromOrders}>
          ⚡ Update Stock
        </button>

        <button onClick={exportProduction}>
          📄 Export PDF
        </button>
      </div>

      {/* 🔍 SEARCH */}
      <input
        className="search"
        placeholder="Search item..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* 🔥 INVENTORY TABLE */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Stock (g)</th>
              <th>Stock (kg)</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map(item => (
              <tr key={item.id}>
                <td>{item.name}</td>

                <td>{item.stockGrams}</td>

                <td>{(item.stockGrams / 1000).toFixed(2)}</td>

                <td>
                  {getStatus(item.stockGrams) === "out" && <span className="out-stock">Out</span>}
                  {getStatus(item.stockGrams) === "low" && <span className="low-stock">Low</span>}
                  {getStatus(item.stockGrams) === "ok" && <span className="in-stock">OK</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 🔥 PRODUCTION TABLE */}
      <div className="production-box">
        <h2>🚚 Tomorrow Production Plan</h2>

        {Object.keys(production).length === 0 ? (
          <p>No orders for tomorrow</p>
        ) : (
          <table className="production-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Grams</th>
                <th>KG</th>
              </tr>
            </thead>

            <tbody>
              {Object.keys(production).map((name, i) => (
                <tr key={i}>
                  <td>{name}</td>
                  <td>{production[name]}</td>
                  <td>{(production[name] / 1000).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      </div>

    </div>
  );
}